import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode
} from 'react'

import { motion } from 'motion/react'

import { cn } from '../../../utils/cn'

interface ScrollMetrics {
  clientHeight: number
  scrollHeight: number
  clientWidth: number
  scrollWidth: number
  scrollTop: number
  scrollLeft: number
}

const EMPTY_METRICS: ScrollMetrics = {
  clientHeight: 0,
  scrollHeight: 0,
  clientWidth: 0,
  scrollWidth: 0,
  scrollTop: 0,
  scrollLeft: 0
}

const MIN_SCROLLBAR_THUMB_SIZE = 24
const SCROLLBAR_WIDTH = 8
const HORIZONTAL_GUTTER = 20
const DRAG_THRESHOLD = 5
const INTERACTIVE_TARGETS =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="combobox"], [contenteditable]:not([contenteditable="false"]), [data-no-drag-scroll]'

interface ContentDrag {
  pointerId: number
  x: number
  scrollLeft: number
  moved: boolean
}

const clamp = (value: number) => Math.min(1, Math.max(0, value))

export interface TableScrollAreaProps {
  /** Fixed height by default; the maximum height when fitContent is enabled. */
  height: number
  fitContent?: boolean
  label: string
  contentKey?: string
  /** Classes for the bordered scrolling frame, such as its radius. */
  className?: string
  children: (
    hasHorizontalScroll: boolean,
    measure: () => void,
    hasScrolledHorizontally: boolean,
    viewportWidth: number
  ) => ReactNode
}

/** The Ømni scrollbars sit 12px outside the bordered grid, in the flow: grid plus vertical track end at the card padding. */
export function TableScrollArea({
  height,
  fitContent = false,
  label,
  contentKey,
  className,
  children
}: TableScrollAreaProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const scrollPosition = useRef({ top: 0, left: 0 })
  const contentRef = useRef<HTMLDivElement>(null)
  const [metrics, setMetrics] = useState<ScrollMetrics>(EMPTY_METRICS)
  const contentDrag = useRef<ContentDrag | null>(null)
  const suppressClick = useRef(false)
  const [dragging, setDragging] = useState(false)

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const next: ScrollMetrics = {
      clientHeight: viewport.clientHeight,
      scrollHeight: viewport.scrollHeight,
      clientWidth: viewport.clientWidth,
      scrollWidth: viewport.scrollWidth,
      scrollTop: viewport.scrollTop,
      scrollLeft: viewport.scrollLeft
    }
    scrollPosition.current = { top: next.scrollTop, left: next.scrollLeft }
    setMetrics(previous =>
      Object.keys(next).every(
        key =>
          previous[key as keyof ScrollMetrics] ===
          next[key as keyof ScrollMetrics]
      )
        ? previous
        : next
    )
  }, [])

  useEffect(() => {
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    if (viewportRef.current) observer.observe(viewportRef.current)
    if (contentRef.current) observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [measure])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollTop = scrollPosition.current.top
    viewport.scrollLeft = scrollPosition.current.left
    measure()
  }, [height, measure])

  useEffect(measure)

  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = 0
    measure()
  }, [contentKey, measure])

  const hasHorizontalScroll = metrics.scrollWidth > metrics.clientWidth + 8
  const hasVerticalScroll = metrics.scrollHeight > metrics.clientHeight + 1
  const maxViewportHeight = Math.max(
    0,
    height - (hasHorizontalScroll ? HORIZONTAL_GUTTER : 0)
  )
  const verticalTrackHeight = fitContent
    ? Math.min(maxViewportHeight, metrics.clientHeight)
    : maxViewportHeight
  const verticalThumbHeight = Math.min(
    verticalTrackHeight,
    Math.max(
      MIN_SCROLLBAR_THUMB_SIZE,
      verticalTrackHeight * (metrics.clientHeight / (metrics.scrollHeight || 1))
    )
  )
  const verticalTravel = Math.max(0, verticalTrackHeight - verticalThumbHeight)
  const verticalRange = Math.max(0, metrics.scrollHeight - metrics.clientHeight)
  const verticalTop = verticalRange
    ? (metrics.scrollTop / verticalRange) * verticalTravel
    : 0

  const horizontalTrackWidth = metrics.clientWidth
  const horizontalThumbWidth = hasHorizontalScroll
    ? Math.min(
        horizontalTrackWidth,
        Math.max(
          MIN_SCROLLBAR_THUMB_SIZE,
          horizontalTrackWidth * (metrics.clientWidth / metrics.scrollWidth)
        )
      )
    : 0
  const horizontalTravel = Math.max(
    0,
    horizontalTrackWidth - horizontalThumbWidth
  )
  const horizontalRange = Math.max(0, metrics.scrollWidth - metrics.clientWidth)
  const horizontalLeft = horizontalRange
    ? (metrics.scrollLeft / horizontalRange) * horizontalTravel
    : 0

  const moveScrollbar = (
    event: PointerEvent<HTMLDivElement>,
    axis: 'vertical' | 'horizontal'
  ) => {
    const viewport = viewportRef.current
    if (!viewport) return
    const rect = event.currentTarget.getBoundingClientRect()
    if (axis === 'vertical' && verticalRange > 0 && verticalTravel > 0) {
      const position = event.clientY - rect.top - verticalThumbHeight / 2
      viewport.scrollTop = clamp(position / verticalTravel) * verticalRange
    }
    if (axis === 'horizontal' && horizontalRange > 0 && horizontalTravel > 0) {
      const position = event.clientX - rect.left - horizontalThumbWidth / 2
      viewport.scrollLeft = clamp(position / horizontalTravel) * horizontalRange
    }
    measure()
  }

  const startDrag = (
    event: PointerEvent<HTMLDivElement>,
    axis: 'vertical' | 'horizontal'
  ) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    moveScrollbar(event, axis)
  }

  const startContentDrag = (event: PointerEvent<HTMLDivElement>) => {
    suppressClick.current = false
    if (
      event.button !== 0 ||
      event.pointerType !== 'mouse' ||
      !hasHorizontalScroll ||
      (event.target instanceof Element &&
        (!event.currentTarget.contains(event.target) ||
          event.target.closest(INTERACTIVE_TARGETS)))
    )
      return
    const viewport = event.currentTarget
    contentDrag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      scrollLeft: viewport.scrollLeft,
      moved: false
    }
    viewport.setPointerCapture(event.pointerId)
    event.preventDefault()
  }

  const moveContentDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = contentDrag.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const dx = event.clientX - drag.x
    if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD) return
    drag.moved = true
    suppressClick.current = true
    setDragging(true)
    event.preventDefault()
    const viewport = event.currentTarget
    viewport.scrollLeft = Math.max(
      0,
      Math.min(
        viewport.scrollWidth - viewport.clientWidth,
        drag.scrollLeft - dx
      )
    )
    measure()
  }

  const endContentDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (contentDrag.current?.pointerId !== event.pointerId) return
    contentDrag.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <div
      data-slot='omni-table-scroll-area'
      className='flex min-w-0 shrink-0 gap-3'
      style={{ height: fitContent ? undefined : height }}
    >
      <div className='flex min-w-0 flex-1 flex-col gap-3'>
        <motion.div
          layoutScroll
          ref={viewportRef}
          data-slot='omni-table-viewport'
          tabIndex={0}
          aria-label={`${label} scroll area`}
          onScroll={measure}
          onPointerDown={startContentDrag}
          onPointerMove={moveContentDrag}
          onPointerUp={endContentDrag}
          onPointerCancel={endContentDrag}
          onLostPointerCapture={endContentDrag}
          onDragStart={event => {
            if (contentDrag.current) event.preventDefault()
          }}
          onClickCapture={event => {
            if (suppressClick.current && event.detail > 0) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          data-dragging={dragging || undefined}
          className={cn(
            'border-surface-200 w-full overflow-auto rounded-lg border [overflow-anchor:none]',
            dragging
              ? 'cursor-grabbing select-none'
              : hasHorizontalScroll && 'cursor-grab',
            className
          )}
          style={{
            height: fitContent ? undefined : maxViewportHeight,
            maxHeight: fitContent ? maxViewportHeight : undefined,
            scrollbarWidth: 'none'
          }}
        >
          <div ref={contentRef} className='min-w-full'>
            {children(
              hasHorizontalScroll,
              measure,
              hasHorizontalScroll && metrics.scrollLeft > 0,
              metrics.clientWidth
            )}
          </div>
        </motion.div>
        {hasHorizontalScroll && (
          <div
            data-slot='omni-table-horizontal-scrollbar'
            aria-hidden='true'
            className='bg-background relative h-2 w-full shrink-0 touch-none rounded-[27px]'
            onPointerDown={event => startDrag(event, 'horizontal')}
            onPointerMove={event => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                moveScrollbar(event, 'horizontal')
              }
            }}
            onPointerUp={event =>
              event.currentTarget.releasePointerCapture(event.pointerId)
            }
          >
            <div
              className='bg-surface-300 absolute inset-y-0 rounded-[37px]'
              style={{ left: horizontalLeft, width: horizontalThumbWidth }}
            />
          </div>
        )}
      </div>
      {hasVerticalScroll && (
        <div
          data-slot='omni-table-vertical-scrollbar'
          aria-hidden='true'
          className='bg-background relative w-2 shrink-0 touch-none overflow-hidden rounded-[27px]'
          style={{ height: verticalTrackHeight, width: SCROLLBAR_WIDTH }}
          onPointerDown={event => startDrag(event, 'vertical')}
          onPointerMove={event => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              moveScrollbar(event, 'vertical')
            }
          }}
          onPointerUp={event =>
            event.currentTarget.releasePointerCapture(event.pointerId)
          }
        >
          <div
            className='bg-surface-300 absolute inset-x-0 rounded-[37px]'
            style={{ top: verticalTop, height: verticalThumbHeight }}
          />
        </div>
      )}
    </div>
  )
}
